import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Check, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { groupsAPI } from '../utils/api';

const InvitePage = () => {
  const { groupId: token } = useParams();   // inviteToken or legacy ObjectId
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const [group, setGroup] = useState(null);
  const [joined, setJoined] = useState(false);
  const [autoJoining, setAutoJoining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const autoJoinFiredRef = useRef(false);

  // Load group info and auto-join if not already a member
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Save invite token so we return here after login/signup
      navigate(`/?redirect=/invite/${token}`, { state: { from: { pathname: `/invite/${token}` } } });
      return;
    }

    const loadAndJoin = async () => {
      try {
        const g = await groupsAPI.previewByToken(token);
        setGroup(g);
        setLoading(false);

        // Auto-join once (guard against double-fire)
        if (autoJoinFiredRef.current) return;
        autoJoinFiredRef.current = true;

        setAutoJoining(true);
        // Small delay so user can see the group name before joining
        await new Promise(r => setTimeout(r, 900));

        const res = await groupsAPI.joinByToken(token);
        setAutoJoining(false);
        setJoined(true);
        if (res.already_member) {
          setTimeout(() => navigate(`/groups/${g.id}`), 1200);
        } else {
          setTimeout(() => navigate('/groups'), 1500);
        }
      } catch (err) {
        setAutoJoining(false);
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : t('invite.error_not_found'));
        setLoading(false);
      }
    };
    loadAndJoin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{error || t('invite.not_found')}</p>
          <button onClick={() => navigate('/home')} className="mt-4 text-slate-900 font-semibold" data-testid="go-home-button">{t('invite.go_home')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full">
        <div className="bg-white p-8 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 transition-colors duration-500
            ${joined ? 'bg-emerald-100 text-emerald-700' : autoJoining ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-700'}`}>
            {joined ? <Check size={40} /> : autoJoining ? <Loader size={36} className="animate-spin" /> : <Users size={40} />}
          </div>

          {joined ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('invite.success')}</h1>
              <p className="text-slate-600 mb-6" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('invite.welcome')} <strong>{group.name}</strong></p>
              <p className="text-sm text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('invite.redirecting')}</p>
            </>
          ) : autoJoining ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.joining')}</h1>
              <p className="text-slate-600 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{group.name}</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('invite.join')}</h1>
              <p className="text-slate-600 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('invite.invited')}</p>
              <p className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{group.name}</p>
              <p className="text-sm text-slate-500 mb-8" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {group.memberCount || 0} {t('invite.members_in')}
              </p>
              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
              <button onClick={() => { autoJoinFiredRef.current = true; }} data-testid="join-group-button"
                className="w-full bg-slate-900 text-white py-4 rounded-full font-bold active:scale-[0.98] transition-transform">
                {t('invite.join_btn')}
              </button>
              <button onClick={() => navigate('/home')} className="w-full mt-3 text-slate-600 py-3 rounded-full font-semibold">{t('invite.later')}</button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InvitePage;
